/**
 * @file useAdminAudienceEnabled.js
 * @description Hook for checking whether the Admin Audience/DataFilter feature is enabled for the current client.
 */

import { useEffect, useState } from 'react';
import { getClientOptions } from '../services/clientService';
import { ClientOptionEnum } from '../utils/data/enums';

let cachedAdminAudienceEnabled = null;
let adminAudienceEnabledRequest = null;
const ADMIN_AUDIENCE_UPDATED_EVENT = 'adminAudienceEnabledUpdated';

const parseAdminAudienceEnabledValue = value => {
    if (typeof value === 'boolean') {
        return value;
    }

    const normalizedValue = String(value || '')
        .trim()
        .toLowerCase();

    return (
        normalizedValue === 'true' ||
        normalizedValue === '1' ||
        normalizedValue === 'enabled'
    );
};

export const publishAdminAudienceEnabledUpdate = value => {
    const nextValue = parseAdminAudienceEnabledValue(value);
    cachedAdminAudienceEnabled = nextValue;
    adminAudienceEnabledRequest = null;
    window.dispatchEvent(
        new CustomEvent(ADMIN_AUDIENCE_UPDATED_EVENT, {
            detail: { enabled: nextValue },
        }),
    );
};

export const useAdminAudienceEnabled = () => {
    const [isAdminAudienceEnabled, setIsAdminAudienceEnabled] = useState(
        Boolean(cachedAdminAudienceEnabled),
    );
    const [isLoadingAdminAudienceEnabled, setIsLoadingAdminAudienceEnabled] =
        useState(cachedAdminAudienceEnabled == null);

    useEffect(() => {
        let isMounted = true;

        const resolveAdminAudienceEnabled = async () => {
            if (cachedAdminAudienceEnabled != null) {
                setIsAdminAudienceEnabled(cachedAdminAudienceEnabled);
                setIsLoadingAdminAudienceEnabled(false);
                return;
            }

            try {
                if (!adminAudienceEnabledRequest) {
                    adminAudienceEnabledRequest = (async () => {
                        const data = await getClientOptions(0, [
                            ClientOptionEnum.AdminAudienceEnabled,
                        ]);
                        const option = data.find(
                            item =>
                                item.clientOptionId ===
                                ClientOptionEnum.AdminAudienceEnabled,
                        );

                        return parseAdminAudienceEnabledValue(option?.value);
                    })();
                }

                const enabled = await adminAudienceEnabledRequest;
                cachedAdminAudienceEnabled = enabled;

                if (isMounted) {
                    setIsAdminAudienceEnabled(enabled);
                }
            } catch (error) {
                console.error(
                    'Unable to determine Admin Audience setting:',
                    error,
                );
                cachedAdminAudienceEnabled = false;

                if (isMounted) {
                    setIsAdminAudienceEnabled(false);
                }
            } finally {
                adminAudienceEnabledRequest = null;

                if (isMounted) {
                    setIsLoadingAdminAudienceEnabled(false);
                }
            }
        };

        resolveAdminAudienceEnabled();

        const handleAdminAudienceUpdated = event => {
            const enabled = Boolean(event?.detail?.enabled);
            setIsAdminAudienceEnabled(enabled);
            setIsLoadingAdminAudienceEnabled(false);
        };

        window.addEventListener(
            ADMIN_AUDIENCE_UPDATED_EVENT,
            handleAdminAudienceUpdated,
        );

        return () => {
            isMounted = false;
            window.removeEventListener(
                ADMIN_AUDIENCE_UPDATED_EVENT,
                handleAdminAudienceUpdated,
            );
        };
    }, []);

    return { isAdminAudienceEnabled, isLoadingAdminAudienceEnabled };
};
